import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// CUSTOM DEFINED HOOK
import useAuth from 'hooks/useAuth';
import useNavigate from 'hooks/useNavigate';
import useLocation from 'hooks/useLocation';
// LAYOUT BASED HOOK
import useLayout from 'layouts/layout-1/context/useLayout';
// CUSTOM COMPONENTS
import SidebarAccordion from './SidebarAccordion';
import { navigations } from 'layouts/layout-parts/navigation';
// CUSTOM STYLED COMPONENTS
import {
  ItemText,
  ListLabel,
  BulletIcon,
  ICON_STYLE,
  ExternalLink,
  NavItemButton
} from 'layouts/layout-1/styles';
import permissionsAccess from '../../../utils/permissionsAccess';

// ===========================================================================

// ===========================================================================

export default function MultiLevelMenu({ sidebarCompact }) {
  const { role } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { handleCloseMobileSidebar } = useLayout();

  // HANDLE ACTIVE CURRENT PAGE
  const activeRoute = (path, path1) => {
    if (pathname === path || pathname === path1) return 1;
    // Check if pathname matches /dashboard/trips with any suffix (list, grid, etc.)
    if (
      path === '/dashboard/trips' &&
      pathname.startsWith('/dashboard/trips/')
    ) {
      return 1;
    }
    return 0;
  };

  // HANDLE NAVIGATE TO ANOTHER PAGE
  const handleNavigation = (path) => {
    navigate(path);
    handleCloseMobileSidebar?.();
  };

  // ACTIVATE SIDEBAR COMPACT
  const COMPACT = sidebarCompact ? 1 : 0;

  // RECURSIVE FUNCTION TO RENDER MULTI LEVEL MENU
  const renderLevels = (data) => {
    return data.map((item, index) => {
      // MENU LABEL DESIGN
      if (item.type === 'label') {
        return (
          <ListLabel key={index} compact={COMPACT}>
            {t(item.label)}
          </ListLabel>
        );
      }

      // MENU LIST WITH CHILDREN
      if (item.children && item.children.length > 0) {
        return (
          <SidebarAccordion key={index} item={item} sidebarCompact={COMPACT}>
            {renderLevels(item.children)}
          </SidebarAccordion>
        );
      }

      // MENU ITEM WITH EXTERNAL LINK
      if (item.type === 'extLink') {
        return (
          <ExternalLink
            key={index}
            href={item.path}
            rel="noopener noreferrer"
            target="_blank"
          >
            <NavItemButton key={item.name} name="child" active={0}>
              {item.icon ? (
                <item.icon sx={ICON_STYLE(0)} />
              ) : (
                <span className="item-icon icon-text">{item.iconText}</span>
              )}

              <ItemText
                compact={COMPACT}
                active={activeRoute(item.path, item?.path1)}
              >
                {item.name}
              </ItemText>
            </NavItemButton>
          </ExternalLink>
        );
      }

      // RENDER REGULAR MENU ITEM
      return (
        <ExternalLink
          key={index}
          href={item.path}
          onClick={(e) => {
            if (e.ctrlKey || e.metaKey || e.button === 1) {
              return;
            }
            e.preventDefault();
            handleNavigation(item.path);
          }}
        >
          <NavItemButton
            disabled={item.disabled}
            active={activeRoute(item.path, item?.path1)}
            component="span"
          >
            {item?.icon ? (
              <item.icon sx={ICON_STYLE(activeRoute(item.path, item?.path1))} />
            ) : (
              <BulletIcon active={activeRoute(item.path, item?.path1)} />
            )}

            <ItemText
              compact={COMPACT}
              active={activeRoute(item.path, item?.path1)}
            >
              {t(item.name)}
            </ItemText>
          </NavItemButton>
        </ExternalLink>
      );
    });
  };

  const filterNavigation = useMemo(() => {
    const filterItems = (items) => {
      if (!items) return [];

      return items
        .filter((item, index) => {
          const { showInMenu, write } = permissionsAccess(role, item.access);

          const hasAccess = item.write ? write : showInMenu;

          const hasChildrenWithAccess = Array.isArray(item.children)
            ? filterItems(item.children).length > 0
            : false;

          if (item.type === 'label') {
            const nextLabelIndex = items
              .slice(index + 1)
              .findIndex((nextItem) => nextItem.type === 'label');

            const nextItems =
              nextLabelIndex === -1
                ? items.slice(index + 1)
                : items.slice(index + 1, index + 1 + nextLabelIndex);

            const nextItemsHaveAccess = nextItems.some((nextItem) => {
              const { showInMenu: nextShowInMenu, write: nextWrite } =
                permissionsAccess(role, nextItem.access);
              const nextHasAccess = nextItem.write
                ? nextWrite
                : nextShowInMenu || nextWrite;
              return (
                nextHasAccess ||
                (Array.isArray(nextItem.children) &&
                  filterItems(nextItem.children).length > 0)
              );
            });

            return nextItemsHaveAccess;
          }

          return hasAccess || hasChildrenWithAccess;
        })
        .map((item) => ({
          ...item,
          children: Array.isArray(item.children)
            ? filterItems(item.children)
            : []
        }));
    };

    return filterItems(navigations);
  }, [role]);

  return <>{renderLevels(filterNavigation)}</>;
}
